/**
 * Workspace Recovery Browser (Spec 205)
 *
 * Lakehouse-parity recovery UX for workspace definitions:
 *  - pick a recovery point on the same RecoveryTimeline the lakehouse/warehouse restore uses,
 *  - explore every captured item — Captured vs Unchanged (a dedup Pointer — incremental backup),
 *    Unsupported, Failed — and diff an item vs the previous point,
 *  - select items and restore them into: the SAME workspace (renamed copy by default), a DIFFERENT
 *    existing workspace, or a NEW temporary workspace.
 *
 * Safety: a restore NEVER blindly overwrites. Same-workspace items already exist, so the default is
 * a renamed copy ("keep both"); overwriting the live items requires an EXPLICIT, confirmed choice.
 *
 * Author: Peter Dobler (Dobler Data Solutions) · Feature 205-workspace-complete-dr
 */

import React, { useMemo, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import {
  Modal,
  DataTable,
  type DataTableColumn,
  Button,
  Badge,
  Checkbox,
  Select,
  LoadingState,
  EmptyState,
} from '@/components/ui';
import { RotateCcw, GitCompare, X } from 'lucide-react';
import {
  useWorkspaceDefinitions,
  useDefinitionPointItems,
  useRestoreItems,
} from '../../hooks/useWorkspaceRestore';
import { WorkspaceRestoreCandidate, DefinitionArtifact } from '../../api/workspaceRestore';
import { restoreApi, AssessedPoint } from '../../api/restore';
import { RecoveryTimeline } from './RecoveryTimeline';
import { DefinitionDiffViewer } from './DefinitionDiffViewer';
import { useAuth } from '../../hooks/useAuth';

interface PointRow {
  definitionRunId: string;
  pointTime: string;
  supportedCount?: number;
  unsupportedCount?: number;
  failedCount?: number;
}

const DIFFABLE = new Set(['Notebook', 'DataPipeline', 'SemanticModel', 'Report', 'Dataflow']);
const isRestorable = (s: DefinitionArtifact['status']) => s === 'Captured' || s === 'Pointer';

const statusBadge = (s: DefinitionArtifact['status']) => {
  switch (s) {
    case 'Captured':
      return <Badge variant="success">captured</Badge>;
    case 'Pointer':
      return <Badge variant="info">unchanged</Badge>;
    case 'Unsupported':
      return <Badge variant="warning">unsupported</Badge>;
    default:
      return <Badge variant="danger">failed</Badge>;
  }
};

const SAME = '__same__';
const TEMP = '__temp__';

export const WorkspaceRecoveryBrowser: React.FC<{
  open: boolean;
  onClose: () => void;
  candidate: WorkspaceRestoreCandidate;
}> = ({ open, onClose, candidate }) => {
  // Spec 214 FR-028: selective definition restore is Admin+Operator server-side.
  const { isOperatorOrAbove } = useAuth();
  const canRestore = isOperatorOrAbove();
  const defs = useWorkspaceDefinitions(open ? candidate.workspaceId : null);
  const points = useMemo<PointRow[]>(() => (defs.data?.points as unknown as PointRow[]) ?? [], [defs.data]);

  const [runId, setRunId] = useState<string | null>(null);
  const selectedRun = runId ?? points[0]?.definitionRunId ?? null;
  const prevRun = useMemo(() => {
    const idx = points.findIndex((p) => p.definitionRunId === selectedRun);
    return idx >= 0 && idx + 1 < points.length ? points[idx + 1].definitionRunId : null;
  }, [points, selectedRun]);

  // Definition points → the shared AssessedPoint timeline (lakehouse format).
  const timelinePoints = useMemo<AssessedPoint[]>(
    () =>
      points.map((p) => ({
        runId: p.definitionRunId,
        entityType: 'lakehouse',
        entityId: candidate.workspaceId,
        pointTime: p.pointTime,
        backupType: 'full',
        restorable: (p.supportedCount ?? 0) > 0,
        notRestorableReason: (p.supportedCount ?? 0) > 0 ? null : 'no captured definitions',
        sizeBytes: null,
        tableCount: p.supportedCount ?? 0,
        immutabilityState: 'none',
      })),
    [points, candidate.workspaceId]
  );

  const itemsQ = useDefinitionPointItems(open ? candidate.workspaceId : null, selectedRun);
  const items = itemsQ.data ?? [];
  const restorableItems = useMemo(() => items.filter((i) => isRestorable(i.status)), [items]);

  const targetsQ = useQuery({ queryKey: ['backup', 'restore-targets'], queryFn: restoreApi.restoreTargets, enabled: open });

  const [checked, setChecked] = useState<Set<string>>(new Set());
  const [diffItem, setDiffItem] = useState<DefinitionArtifact | null>(null);
  const [target, setTarget] = useState<string>(SAME);
  const [confirming, setConfirming] = useState(false);
  const [result, setResult] = useState<string | null>(null);
  const [restoring, setRestoring] = useState(false); // true while polling the container to completion
  const restore = useRestoreItems();

  const reset = () => {
    setChecked(new Set());
    setDiffItem(null);
    setConfirming(false);
    setResult(null);
  };

  const toggle = (id: string) =>
    setChecked((s) => {
      const n = new Set(s);
      n.has(id) ? n.delete(id) : n.add(id);
      return n;
    });
  const allChecked = restorableItems.length > 0 && restorableItems.every((i) => checked.has(i.itemId));
  const toggleAll = () => setChecked(() => (allChecked ? new Set() : new Set(restorableItems.map((i) => i.itemId))));

  const targetOptions = [
    { value: SAME, label: 'Same workspace (renamed copy)' },
    ...(targetsQ.data ?? [])
      .filter((t) => t.workspaceId !== candidate.workspaceId)
      .map((t) => ({ value: t.workspaceId, label: `Into: ${t.workspaceName}` })),
    { value: TEMP, label: 'New temporary workspace' },
  ];
  const targetLabel = targetOptions.find((o) => o.value === target)?.label ?? 'Same workspace';
  const isSameWs = target === SAME;

  // Poll the container execution to a terminal state so the modal acknowledges completion
  // (the restore runs async ~1 min) instead of sitting on "running".
  const pollToDone = async (executionName: string, count: number) => {
    const isTerminal = (s: string) => /succeeded|completed|failed|stopped|degraded|error|cancel/i.test(s);
    for (let i = 0; i < 48; i++) {
      await new Promise((r) => setTimeout(r, 5000));
      let st = '';
      try {
        const prog = await restoreApi.progress(executionName);
        st = String(prog?.status ?? '');
      } catch {
        continue; // transient — keep polling
      }
      if (isTerminal(st)) {
        const ok = /succeeded|completed/i.test(st);
        if (ok) setChecked(new Set()); // clear the selection only once the restore has actually completed
        setResult(
          ok
            ? `✅ Restore complete — ${count} item(s) → ${targetLabel}. Open the workspace to see them. (Paginated reports may need their data connection reconnected.)`
            : `❌ Restore ${st.toLowerCase()} — ${count} item(s) → ${targetLabel}. Check the target workspace / Activity for details.`
        );
        return;
      }
      setResult(`⏳ Restore running (${st || 'starting'}) — ${count} item(s) → ${targetLabel}…`);
    }
    setResult(`⏳ Still running — ${count} item(s) → ${targetLabel}. Check the target workspace shortly.`);
  };

  const doRestore = async () => {
    if (!selectedRun || checked.size === 0) return;
    setResult(null);
    const count = checked.size;
    setRestoring(true);
    try {
      const outcome = await restore.mutateAsync({
        workspaceId: candidate.workspaceId,
        definitionRunId: selectedRun,
        itemIds: [...checked],
        targetWorkspaceId: isSameWs || target === TEMP ? undefined : target,
        createTempWorkspace: target === TEMP,
      });
      setConfirming(false);
      // Only "launched" with a real run id is a true success — otherwise surface the reason rather
      // than silently claiming success (a restore that lies about success is dangerous).
      if (outcome.status === 'launched' && outcome.restoreRunId) {
        // Keep the selection visible during the run (so the footer reads "1 of 6", consistent with
        // "restoring 1 item"); it's cleared in pollToDone once the restore actually completes.
        if (outcome.executionName) {
          setResult(`⏳ Restore running — ${count} item(s) → ${targetLabel}…`);
          await pollToDone(outcome.executionName, count);
        } else {
          setResult(`✅ Restore started — ${count} item(s) → ${targetLabel}. It runs in the background (~1 min); check the target workspace shortly.`);
        }
      } else if (outcome.status === 'queued') {
        setResult(`⏳ Queued: ${outcome.reason ?? 'at restore concurrency cap'}. It will start when a slot frees.`);
      } else {
        setResult(`⚠️ Not started: ${outcome.reason ?? 'the restore was refused'}.`);
      }
    } catch (e) {
      setConfirming(false);
      setResult(`❌ Restore failed to start: ${(e as Error)?.message ?? 'request error'}.`);
    } finally {
      setRestoring(false);
    }
  };

  const columns: DataTableColumn<DefinitionArtifact>[] = [
    {
      key: 'sel',
      header: <Checkbox checked={allChecked} onChange={toggleAll} aria-label="Select all restorable" />,
      width: '2.5rem',
      sortable: false,
      render: (i) =>
        isRestorable(i.status) ? (
          <Checkbox checked={checked.has(i.itemId)} onChange={() => toggle(i.itemId)} aria-label={`Select ${i.itemName ?? i.itemId}`} />
        ) : null,
    },
    { key: 'itemType', header: 'Type', width: '9rem', render: (i) => <span className="text-sm">{i.itemType}</span> },
    { key: 'itemName', header: 'Name', render: (i) => <span className="font-medium text-[var(--color-text-primary)]">{i.itemName ?? i.itemId}</span> },
    { key: 'itemVersion', header: 'Ver', width: '4rem', render: (i) => <span className="text-sm text-[var(--color-text-secondary)]">v{i.itemVersion}</span> },
    { key: 'status', header: 'State', width: '8rem', sortable: false, render: (i) => statusBadge(i.status) },
    {
      key: 'changes',
      header: '',
      width: '7rem',
      align: 'right',
      sortable: false,
      render: (i) =>
        prevRun && DIFFABLE.has(i.itemType) ? (
          <Button variant="ghost" size="sm" onClick={() => setDiffItem(i)}>
            <GitCompare className="h-4 w-4" /> Changes
          </Button>
        ) : null,
    },
  ];

  return (
    <Modal isOpen={open} onClose={onClose} title={`Recovery browser — ${candidate.workspaceName}`} size="xl">
      <div className="space-y-4">
        <p className="text-sm text-[var(--color-text-secondary)]">
          Pick a recovery point, see what was captured, then restore selected items. Items marked{' '}
          <Badge variant="info">unchanged</Badge> were deduplicated (not re-stored) — incremental backup. A restore
          <strong> never overwrites anything</strong>: it always brings items back as new ones, using a{' '}
          <strong>renamed copy</strong> wherever the name is already taken. Your current items are never touched.
        </p>

        {defs.isLoading ? (
          <LoadingState />
        ) : points.length === 0 ? (
          <EmptyState title="No recovery points for this workspace yet" />
        ) : (
          <RecoveryTimeline points={timelinePoints} selectedRunId={selectedRun} onSelect={(p) => { setRunId(p.runId); reset(); }} />
        )}

        {selectedRun &&
          (itemsQ.isLoading ? (
            <LoadingState />
          ) : items.length === 0 ? (
            <EmptyState title="No items captured at this point" />
          ) : (
            <DataTable columns={columns} rows={items} rowKey={(i) => i.itemId} />
          ))}

        {diffItem && prevRun && selectedRun && (
          <div className="space-y-2">
            <div className="flex items-center justify-between">
              <span className="text-sm font-medium text-[var(--color-text-primary)]">
                Changes in “{diffItem.itemName ?? diffItem.itemId}” since the previous capture
              </span>
              <Button variant="ghost" size="sm" onClick={() => setDiffItem(null)}>
                <X className="h-4 w-4" /> Close diff
              </Button>
            </div>
            <DefinitionDiffViewer workspaceId={candidate.workspaceId} fromRunId={prevRun} toRunId={selectedRun} itemId={diffItem.itemId} />
          </div>
        )}

        {result && <p className="text-sm text-[var(--color-text-primary)]">{result}</p>}

        {/* Confirm step — restore target. 🛑 There is deliberately no overwrite choice here: the
            "Overwrite the existing items (cannot be undone)" radio was REMOVED 2026-07-17 by owner
            decision. It reached the container as UPDATE_EXISTING and replaced the live item's
            definition. A restore never destroys live data — it always lands beside it. */}
        {confirming ? (
          <div className="space-y-3 rounded-md border border-[var(--color-border)] bg-[var(--color-bg-subtle)] p-3">
            <div className="text-sm text-[var(--color-text-primary)]">
              Restore <strong>{checked.size}</strong> item(s) → <strong>{targetLabel}</strong>.
            </div>
            {isSameWs ? (
              <div className="text-sm text-[var(--color-text-secondary)]">
                These items already exist here, so they come back as <strong>renamed copies</strong> alongside them.
                Your current items are not touched.
              </div>
            ) : target === TEMP ? (
              <div className="text-sm text-[var(--color-text-secondary)]">A new temporary workspace will be created and the items restored into it.</div>
            ) : (
              <div className="text-sm text-[var(--color-text-secondary)]">Items are restored into the chosen workspace. Any name conflicts are kept as renamed copies (never overwritten).</div>
            )}
            <div className="flex justify-end gap-2">
              <Button variant="secondary" onClick={() => setConfirming(false)}>Back</Button>
              <Button variant="primary" disabled={restore.isPending || restoring} onClick={doRestore}>
                <RotateCcw className="h-4 w-4" /> {restore.isPending || restoring ? 'Restoring…' : `Restore ${checked.size}`}
              </Button>
            </div>
          </div>
        ) : (
          <div className="flex flex-wrap items-end justify-between gap-3 border-t border-[var(--color-border)] pt-3">
            <div className="flex items-end gap-3">
              <div className="w-72">
                <Select label="Restore to" value={target} onChange={(e) => setTarget(e.target.value)}>
                  {targetOptions.map((o) => (
                    <option key={o.value} value={o.value}>{o.label}</option>
                  ))}
                </Select>
              </div>
              <span className="pb-2 text-sm text-[var(--color-text-secondary)]">
                {restoring
                  ? `Restoring ${checked.size} item(s)…`
                  : `${checked.size} of ${restorableItems.length} restorable item(s) selected`}
              </span>
            </div>
            <div className="flex gap-2">
              <Button variant="secondary" onClick={onClose}>Close</Button>
              {canRestore && (
                <Button variant="primary" disabled={checked.size === 0 || restoring} onClick={() => setConfirming(true)}>
                  <RotateCcw className="h-4 w-4" /> {restoring ? 'Restoring…' : 'Restore selected…'}
                </Button>
              )}
            </div>
          </div>
        )}
      </div>
    </Modal>
  );
};

export default WorkspaceRecoveryBrowser;
